import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslation } from "@/i18n/translations";
import { useInviteUserToOrganizationMutation } from "@/hooks/queries/useOrganizationUsersQuery";

interface InviteOrganizationUserDialogProps {
  organizationId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const InviteOrganizationUserDialog = ({ organizationId, open, onOpenChange }: InviteOrganizationUserDialogProps) => {
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const [emailInput, setEmailInput] = useState("");
  const inviteMutation = useInviteUserToOrganizationMutation();

  const handleInvite = () => {
    const trimmed = emailInput.trim();
    if (!trimmed) return;
    inviteMutation.mutate(
      { organizationId, email: trimmed },
      { onSuccess: () => { setEmailInput(""); onOpenChange(false); } }
    );
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { onOpenChange(next); if (!next) setEmailInput(""); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("inviteMember")}</DialogTitle>
          <DialogDescription>{t("inviteMemberDesc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="invite-organization-user-email">{t("email")}</Label>
          <Input
            id="invite-organization-user-email"
            type="email"
            placeholder={t("enterUserEmail")}
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleInvite(); }}
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("cancel")}</Button>
          <Button onClick={handleInvite} disabled={!emailInput.trim() || inviteMutation.isPending}>
            {inviteMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {t("inviteMember")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InviteOrganizationUserDialog;
